import * as React from "react";
import { lazy } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { DataContext } from "../../providers";
import {
	RouteEntry,
	RedirectEntry,
	adminRoutes,
	superAdminExtraRoutes,
	customerRoutes,
	customerRedirects,
} from "../route-config";

const NotFoundView = lazy(() => import("./NotFoundView"));

/** Renders a list of RouteEntry items as v6 Route elements */
function renderRoutes(routes: RouteEntry[], access?: IUserTypeAccess) {
	return routes.map(({ path, component: Component, props, guard, errorElement }) => {
		if (guard && access && !guard(access)) return null;
		return <Route key={path} path={path} element={<Component path={path} {...props} />} errorElement={errorElement} />;
	});
}

/** Renders redirect entries as v6 Navigate elements */
function renderRedirects(redirects: RedirectEntry[]) {
	return redirects.map(({ from, to }) => (
		<Route key={from} path={from} element={<Navigate replace to={to} />} />
	));
}

/**
 * AuthGuard — renders admin or customer routes based on auth state.
 * Redirects unauthenticated users to login.
 */
export function AuthGuard() {
	const data = React.useContext(DataContext);

	if (!data.token || !data.currentUser) {
		return <Navigate replace to="/" />;
	}

	// Admin users
	if (data.currentUser.superAdmin) {
		const isSuperAdmin = data.currentUser.email === "juha@weatherproof.se";
		return (
			<Routes>
				{isSuperAdmin && renderRoutes(superAdminExtraRoutes)}
				{renderRoutes(adminRoutes)}
				<Route path="*" element={<NotFoundView />} />
			</Routes>
		);
	}

	// Customer users
	return (
		<Routes>
			{renderRoutes(customerRoutes, data.currentUserTypeAccess)}
			{renderRedirects(customerRedirects)}
			<Route path="*" element={<NotFoundView />} />
		</Routes>
	);
}
